import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle2, AlertCircle, MailCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { API } from '../config/api';

const LOGO = "/favicon-512.png";

export default function ConfirmEmail() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState('loading'); // loading | success | already | expired | error
  const [name, setName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Resend flow
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const [resendError, setResendError] = useState('');

  useEffect(() => {
    (async () => {
      if (!token) {
        setState('expired');
        setErrorMsg('Aucun token de confirmation détecté.');
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/confirm?token=${encodeURIComponent(token)}`);
        setName(res.data?.name || '');
        setState(res.data?.already_confirmed ? 'already' : 'success');
      } catch (err) {
        const detail = err?.response?.data?.detail || '';
        setErrorMsg(detail || "Une erreur s'est produite. Veuillez réessayer.");
        // Backend now returns "Lien expiré ou invalide..." for both unknown and expired tokens
        if (/expir/i.test(detail) || /invalide/i.test(detail)) {
          setState('expired');
        } else {
          setState('error');
        }
      }
    })();
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    setResendError('');
    if (!resendEmail || !resendEmail.includes('@')) {
      setResendError('Veuillez saisir une adresse email valide.');
      return;
    }
    setResendLoading(true);
    try {
      await axios.post(`${API}/auth/resend-confirmation`, { email: resendEmail.trim().toLowerCase() });
      setResendDone(true);
    } catch (err) {
      setResendError("Connexion au serveur impossible. Réessayez dans un instant.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5EFE5] flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO} alt="PURO" className="h-14 w-auto mx-auto mb-5" />
          <div className="w-8 h-0.5 bg-[#C99A55] mx-auto mt-3" />
        </div>

        <Card className="border-[#E9DDCC] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <CardContent className="p-8 text-center">
            {state === 'loading' && (
              <>
                <Loader2 className="w-8 h-8 animate-spin text-[#C99A55] mx-auto mb-5" />
                <p className="text-[#7A6455]">Vérification de votre lien…</p>
              </>
            )}
            {(state === 'success' || state === 'already') && (
              <>
                <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#C99A55]/15 flex items-center justify-center">
                  <CheckCircle2 className="w-7 h-7 text-[#C99A55]" strokeWidth={1.5} />
                </div>
                <h1 className="font-heading text-2xl text-[#493329] mb-3" data-testid="confirm-success-title">
                  {state === 'success' ? 'Pré-inscription confirmée' : 'Déjà confirmée'}
                </h1>
                <p className="text-sm text-[#7A6455] leading-relaxed mb-6">
                  {state === 'success'
                    ? `Bienvenue${name ? `, ${name.split(' ')[0]}` : ''} ! Votre place est désormais sécurisée. Vous recevez un email de bienvenue dans quelques instants.`
                    : `Votre pré-inscription est déjà active. Vous pouvez accéder à votre espace personnel.`}
                </p>
                <Link to="/login">
                  <Button className="bg-[#C99A55] hover:bg-[#493329] text-white rounded-xl h-11 px-8" data-testid="confirm-go-login">
                    Accéder à mon espace
                  </Button>
                </Link>
              </>
            )}
            {state === 'expired' && (
              <>
                <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#C99A55]/15 flex items-center justify-center">
                  <MailCheck className="w-7 h-7 text-[#C99A55]" strokeWidth={1.5} />
                </div>
                <h1 className="font-heading text-2xl text-[#493329] mb-3" data-testid="confirm-expired-title">
                  Lien expiré
                </h1>
                <p className="text-sm text-[#7A6455] leading-relaxed mb-6">
                  {errorMsg || "Ce lien de confirmation n'est plus valable."}
                  <br />
                  Recevez un nouveau lien sécurisé en quelques secondes.
                </p>
                {resendDone ? (
                  <div className="bg-[#F0E8D9] rounded-xl p-5 text-left" data-testid="resend-confirmation-success">
                    <p className="text-sm text-[#493329] font-medium mb-1.5">Vérifiez votre boîte mail</p>
                    <p className="text-xs text-[#7A6455] leading-relaxed">
                      Si un compte non confirmé existe avec <strong className="text-[#493329]">{resendEmail}</strong>, un nouveau lien vient d'être envoyé. Pensez à vérifier vos spams.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleResend} className="space-y-4 text-left" data-testid="resend-confirmation-form">
                    <div className="space-y-2">
                      <Label htmlFor="resend-email" className="text-[#493329] text-sm">Votre email de pré-inscription</Label>
                      <Input
                        id="resend-email" type="email" required
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        className="h-11 rounded-xl border-[#E9DDCC] bg-white"
                        placeholder="vous@exemple.com"
                        data-testid="resend-email-input"
                      />
                    </div>
                    {resendError && (
                      <p className="text-xs text-[#C76542]">{resendError}</p>
                    )}
                    <Button
                      type="submit"
                      className="w-full bg-[#C99A55] hover:bg-[#493329] text-white rounded-xl h-11"
                      disabled={resendLoading}
                      data-testid="resend-confirmation-submit"
                    >
                      {resendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Recevoir un nouveau lien"}
                    </Button>
                  </form>
                )}
              </>
            )}
            {state === 'error' && (
              <>
                <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#C76542]/15 flex items-center justify-center">
                  <AlertCircle className="w-7 h-7 text-[#C76542]" strokeWidth={1.5} />
                </div>
                <h1 className="font-heading text-2xl text-[#493329] mb-3">Une erreur est survenue</h1>
                <p className="text-sm text-[#7A6455] leading-relaxed mb-6">{errorMsg}</p>
                <Link to="/register">
                  <Button className="bg-[#C99A55] hover:bg-[#493329] text-white rounded-xl h-11 px-8">
                    Recommencer une pré-inscription
                  </Button>
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
